import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import type { OverlayStyleOverride } from '../types/persistence'
import { clampQuickBarPosition } from './quickBarUtils'
import { LineStyleControl } from './LineStyleControl'
import {
  COORDINATE_EDITABLE,
  coordinateFields,
  normalizePoints,
  patchAnchorPoints,
  tsToLocalInput,
  localInputToTs,
} from './overlayCoordinates'

/**
 * Floating quick-edit bar for the SELECTED drawing.
 *
 * Appears on single-click selection (double-click still opens the full settings
 * drawer). Everything here acts on the live overlay immediately — there is no
 * apply/cancel, matching the reference app.
 *
 * Draggable by the grip so it can be moved off whatever it is covering; the
 * position is clamped to the chart so it can never be parked out of reach.
 */

const SWATCHES = [
  '#eab308',
  '#2962ff',
  '#089981',
  '#f23645',
  '#ff9800',
  '#9c27b0',
  '#ffffff',
  '#787b86',
]

export interface OverlayQuickBarProps {
  /** Current style of the selected drawing. */
  styles: OverlayStyleOverride | undefined
  /** Overlay type name — gates the coordinate control (static line/number tools). */
  overlayName?: string
  /** The selected drawing's points (anchor first). Drives the coordinate editor. */
  points?: Array<{ timestamp?: number; value?: number }>
  /** Live-apply a coordinate edit (rebuilt points array). Persists via the host. */
  onApplyPoints?: (points: Array<{ timestamp: number; value: number }>) => void
  locked: boolean
  /** The chart area. Measured live during a drag — a snapshot object would be
   *  a new value every render (re-subscribing the drag listeners) and is 0×0 on
   *  the first render, which pins the bar to the top-left corner. */
  chartRef: { current: HTMLElement | null }
  onApplyStyle: (patch: OverlayStyleOverride) => void
  onToggleLock: () => void
  onDuplicate: () => void
  onDelete: () => void
  /** Opens the full settings drawer — the quick bar is a subset. */
  onOpenSettings?: () => void
  /** The tool's own colour, for drawings that paint themselves and so carry no
   *  style override. Undefined ⇒ the swatch shows an explicit unset state
   *  rather than inventing a colour. */
  toolColor?: string
  /** Initial distance from the top of the chart, px. Set clear of the chart's
   *  own top toolbar so the two do not compete. */
  topOffset?: number
}

const Divider = () => <span className="mx-0.5 h-4 w-px bg-[hsl(var(--chart-text)/0.18)]" />

function IconButton({
  title,
  onClick,
  active,
  children,
}: {
  title: string
  onClick: () => void
  active?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      title={title}
      aria-label={title}
      onClick={onClick}
      className={
        'flex h-6 w-6 items-center justify-center rounded-sm chrome-press ' +
        (active
          ? 'bg-accent text-[hsl(var(--chart-text))]'
          : 'text-[hsl(var(--chart-text)/0.85)] chrome-press hover:bg-accent hover:text-[hsl(var(--chart-text))]')
      }
    >
      {children}
    </button>
  )
}

/** Small popover anchored under a control. */
function Popover({ children }: { children: React.ReactNode }) {
  return (
    <div className="absolute left-0 top-full z-10 mt-1 flex items-center gap-1 rounded-md border border-[hsl(var(--chart-text)/0.15)] bg-popover p-1.5 shadow-lg shadow-black/40">
      {children}
    </div>
  )
}

/**
 * The element the bar is actually positioned against.
 *
 * The bar is `position: absolute`, so its coordinates are relative to its
 * nearest positioned ancestor — NOT necessarily the chart canvas container we
 * were handed. Using the wrong element mixes coordinate spaces.
 */
function positionParent(el: HTMLElement | null, fallback: HTMLElement | null): HTMLElement | null {
  return (el?.offsetParent as HTMLElement | null) ?? fallback
}

export function OverlayQuickBar({
  styles,
  overlayName,
  points,
  onApplyPoints,
  locked,
  chartRef,
  onApplyStyle,
  onToggleLock,
  onDuplicate,
  onDelete,
  onOpenSettings,
  topOffset = 44,
  toolColor,
}: OverlayQuickBarProps) {
  const ref = useRef<HTMLDivElement>(null)
  // null until measured — see the centering effect below.
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null)
  const [menu, setMenu] = useState<'color' | 'width' | 'opacity' | 'coords' | null>(null)
  const drag = useRef<{ dx: number; dy: number } | null>(null)

  // The coordinate editor shows only for static line/number tools (horizontal
  // line, horizontal ray, vertical line) and only when the host wires up points.
  const showCoords = !!overlayName && !!onApplyPoints && COORDINATE_EDITABLE.has(overlayName)
  const fields = overlayName ? coordinateFields(overlayName) : { price: false, time: false }
  const anchor = normalizePoints(points)[0] ?? { timestamp: 0, value: 0 }
  const applyAnchor = (patch: { timestamp?: number; value?: number }) => {
    onApplyPoints?.(patchAnchorPoints(normalizePoints(points), patch))
  }

  // Only an EXPLICIT override, then the tool's own colour. Never a guess — a
  // swatch showing yellow on a blue drawing is worse than showing nothing.
  const color = styles?.color ?? toolColor

  // Park the bar top-CENTRE on first measure. Defaulting to a fixed {16,12}
  // put it over the OHLC readout in the top-left, which is exactly where the
  // chart's own information lives.
  useLayoutEffect(() => {
    if (pos !== null) return
    const el = ref.current
    const host = positionParent(el, chartRef.current)
    if (!el || !host) return
    setPos({ x: Math.max(0, (host.clientWidth - el.offsetWidth) / 2), y: topOffset })
  }, [pos, chartRef, topOffset])

  // Drag via POINTER CAPTURE. Capturing routes every subsequent pointer event
  // to the grip, so the chart canvas never sees the drag — without it, moving
  // across the chart reaches klinecharts' own handlers and the drawing gets
  // deselected mid-drag, unmounting the bar under the cursor.
  const startDrag = useCallback((e: React.PointerEvent) => {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top }
    e.currentTarget.setPointerCapture(e.pointerId)
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const onDragMove = useCallback(
    (e: React.PointerEvent) => {
      const el = ref.current
      const host = positionParent(el, chartRef.current)
      if (!drag.current || !el || !host) return
      // `clientX/Y` are VIEWPORT coords; `left/top` on an absolutely-positioned
      // element are OFFSET-PARENT coords. Without subtracting the parent's
      // origin the bar jumps by the chart's page offset the instant you grab it
      // — and then clamps hard into the far corner.
      const host_r = host.getBoundingClientRect()
      setPos(
        clampQuickBarPosition(
          {
            x: e.clientX - host_r.left - drag.current.dx,
            y: e.clientY - host_r.top - drag.current.dy,
          },
          { width: el.offsetWidth, height: el.offsetHeight },
          // Measured live: the pane resizes, and a stale snapshot lets the bar
          // be dragged outside a shrunken chart.
          { width: host.clientWidth, height: host.clientHeight },
        ),
      )
    },
    [chartRef],
  )

  const endDrag = useCallback((e: React.PointerEvent) => {
    drag.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
  }, [])

  const toggle = (m: 'color' | 'coords') => setMenu((cur) => (cur === m ? null : m))

  const coordInputCls =
    'h-6 rounded border border-border-strong bg-field px-1.5 text-[11px] text-[hsl(var(--chart-text))] outline-hidden focus:border-primary'

  return (
    <div
      ref={ref}
      data-testid="overlay-quick-bar"
      // Marks this as chart chrome so ChartManager's capture-phase
      // outside-click deselect leaves it alone (see outsideDeselectHandler).
      data-chart-chrome=""
      style={{ left: pos?.x ?? 0, top: pos?.y ?? 0, visibility: pos ? 'visible' : 'hidden' }}
      className="absolute z-50 flex items-center gap-1 rounded-lg border border-[hsl(var(--chart-text)/0.15)] bg-[hsl(var(--chart-canvas)/0.95)] px-1.5 py-1 shadow-lg shadow-black/40 backdrop-blur-md"
      // Clicks inside must not reach the chart, or selecting a control would
      // deselect the very drawing being edited.
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Grip */}
      <span
        title="Drag to move"
        onPointerDown={startDrag}
        onPointerMove={onDragMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className="flex h-6 w-3 cursor-grab items-center justify-center text-[hsl(var(--chart-text)/0.5)] active:cursor-grabbing"
      >
        <svg width={8} height={14} viewBox="0 0 8 14" fill="currentColor">
          <circle cx="2" cy="3" r="1" />
          <circle cx="6" cy="3" r="1" />
          <circle cx="2" cy="7" r="1" />
          <circle cx="6" cy="7" r="1" />
          <circle cx="2" cy="11" r="1" />
          <circle cx="6" cy="11" r="1" />
        </svg>
      </span>

      {/* Coordinates — static line/number tools only (price and/or time of the
          anchor point). The discoverable twin of the drawer's Coordinates section. */}
      {showCoords && (
        <>
          <div className="relative">
            <IconButton
              title="Coordinates"
              onClick={() => toggle('coords')}
              active={menu === 'coords'}
            >
              <svg width={14} height={14} viewBox="0 0 14 14" fill="none" stroke="currentColor">
                <circle cx="7" cy="7" r="2" strokeWidth={1.3} />
                <path
                  d="M7 1v2.5M7 10.5V13M1 7h2.5M10.5 7H13"
                  strokeWidth={1.3}
                  strokeLinecap="round"
                />
              </svg>
            </IconButton>
            {menu === 'coords' && (
              <Popover>
                <div className="flex flex-col gap-1.5">
                  {fields.price && (
                    <label className="flex items-center gap-2 text-[11px] text-[hsl(var(--chart-text)/0.85)]">
                      <span className="w-9">Price</span>
                      <input
                        type="number"
                        step="any"
                        aria-label="Price"
                        value={anchor.value}
                        onChange={(e) => {
                          const raw = e.target.value
                          if (raw === '') return
                          const n = Number(raw)
                          if (!Number.isFinite(n)) return
                          applyAnchor({ value: n })
                        }}
                        className={coordInputCls + ' w-28'}
                      />
                    </label>
                  )}
                  {fields.time && (
                    <label className="flex items-center gap-2 text-[11px] text-[hsl(var(--chart-text)/0.85)]">
                      <span className="w-9">Time</span>
                      <input
                        type="datetime-local"
                        aria-label="Time"
                        value={tsToLocalInput(anchor.timestamp)}
                        onChange={(e) => {
                          const ts = localInputToTs(e.target.value)
                          if (ts != null) applyAnchor({ timestamp: ts })
                        }}
                        className={coordInputCls + ' w-40'}
                      />
                    </label>
                  )}
                </div>
              </Popover>
            )}
          </div>
          <Divider />
        </>
      )}

      {/* Colour */}
      <div className="relative">
        <button
          title="Colour"
          aria-label="Colour"
          onClick={() => toggle('color')}
          className="h-4 w-4 rounded-[3px] border border-[hsl(var(--chart-text)/0.25)]"
          style={
            color
              ? { background: color }
              : // Unset: a muted diagonal, meaning "this drawing uses its own
                // colours". Picking a swatch replaces them.
                {
                  background:
                    'repeating-linear-gradient(45deg, hsl(var(--chart-text)/0.25) 0 2px, transparent 2px 4px)',
                }
          }
        />
        {menu === 'color' && (
          <Popover>
            {SWATCHES.map((c) => (
              <button
                key={c}
                title={c}
                aria-label={c}
                onClick={() => {
                  onApplyStyle({ color: c })
                  setMenu(null)
                }}
                className="h-5 w-5 rounded border border-[hsl(var(--chart-text)/0.25)]"
                style={{ background: c }}
              />
            ))}
          </Popover>
        )}
      </div>

      <Divider />

      {/* Line — weight + style + transparency, consolidated into one popover.
          Overlays model only solid/dashed; opacity maps to `fillOpacity`. */}
      <LineStyleControl
        size={styles?.size ?? 2}
        style={styles?.style ?? 'solid'}
        opacity={styles?.fillOpacity}
        styleOptions={['solid', 'dashed']}
        triggerClassName="flex h-6 items-center gap-1 rounded-sm px-1 text-[hsl(var(--chart-text)/0.85)] chrome-press hover:bg-accent"
        onChange={(patch) =>
          onApplyStyle({
            ...(patch.size !== undefined ? { size: patch.size } : {}),
            ...(patch.style !== undefined
              ? { style: patch.style === 'dotted' ? 'dashed' : patch.style }
              : {}),
            ...(patch.opacity !== undefined ? { fillOpacity: patch.opacity } : {}),
          })
        }
      />

      <Divider />

      <IconButton title="Duplicate" onClick={onDuplicate}>
        <svg width={14} height={14} viewBox="0 0 14 14" fill="none" stroke="currentColor">
          <rect x="4.5" y="4.5" width="8" height="8" rx="1.2" strokeWidth={1.3} />
          <path d="M9.5 1.5H2.2A.7.7 0 0 0 1.5 2.2v7.3" strokeWidth={1.3} />
        </svg>
      </IconButton>

      <IconButton title={locked ? 'Unlock' : 'Lock'} onClick={onToggleLock} active={locked}>
        <svg width={14} height={14} viewBox="0 0 14 14" fill="none" stroke="currentColor">
          <rect x="2.5" y="6" width="9" height="6.5" rx="1.2" strokeWidth={1.3} />
          {locked ? (
            <path d="M4.5 6V4.2a2.5 2.5 0 0 1 5 0V6" strokeWidth={1.3} />
          ) : (
            <path d="M4.5 6V4.2a2.5 2.5 0 0 1 5 0" strokeWidth={1.3} />
          )}
        </svg>
      </IconButton>

      {onOpenSettings && (
        <IconButton title="Settings" onClick={onOpenSettings}>
          {/* lucide `settings-2` (two rails with knobs), drawn natively in the
              bar's 14-unit space so its stroke weight matches its neighbours.
              Replaced a circle-plus-four-ticks mark that read as a sun/asterisk
              rather than as settings. Rails stop short of their knobs so the
              strokes never double up. */}
          <svg width={14} height={14} viewBox="0 0 14 14" fill="none" stroke="currentColor">
            <circle cx="4.2" cy="4.2" r="1.8" strokeWidth={1.3} />
            <path d="M6.6 4.2H12" strokeWidth={1.3} strokeLinecap="round" />
            <circle cx="9.8" cy="9.8" r="1.8" strokeWidth={1.3} />
            <path d="M7.4 9.8H2" strokeWidth={1.3} strokeLinecap="round" />
          </svg>
        </IconButton>
      )}

      <IconButton title="Delete" onClick={onDelete}>
        <svg width={14} height={14} viewBox="0 0 14 14" fill="none" stroke="currentColor">
          <path d="M2.5 4h9M5.5 4V2.6h3V4M4 4l.6 8h4.8L10 4" strokeWidth={1.3} />
        </svg>
      </IconButton>
    </div>
  )
}
